/**
 * post-auth-route v1.0 (2026-09-30, cc_tasks row 310, T344)
 *
 * WHAT THE PERSON WAS TRYING TO DO COMES FIRST. ONBOARDING COMES AFTER.
 *
 * Live test 2026-09-30: /auth?next=/pricing -> Continue with LinkedIn -> signed in, and
 * landed in onboarding instead of /pricing. Two causes, both fixed here and in Auth.tsx:
 *  1. /pricing is a static document (public/pricing.html). The post-auth code sent it
 *     through the React router, which has no /pricing route.
 *  2. Auth.tsx runs its post-auth handler twice (the "already signed in" effect and the
 *     SIGNED_IN listener). The first run consumed the stashed next and left; the second
 *     found no next, saw onboarding unfinished, and sent the person to /onboarding.
 * And every onboarding gate (App.tsx AppLayout, ProtectedRoute, the page-level checks)
 * pulled an unfinished account into onboarding even when it had just been sent to the
 * page it asked for. Those gates now stand down for the rest of the tab session once the
 * router has sent someone to their destination (onboardingDeferred()), and AppLayout
 * shows a non-blocking "Finish setup" banner instead.
 *
 * The decision itself is pure (decidePostAuthRoute, sanitizeNext) and tested by
 * scripts/post-auth-route-test.mjs. The storage helpers below it touch the browser.
 */

/** Paths the post-auth router must never return to: going back to the login page is a loop. */
const LOOP_PATHS = new Set(["/auth", "/login", "/signin", "/signup"]);

/**
 * A same-origin path, or null. Accepts only a value that starts with exactly one "/" and
 * contains no backslash, no control character or whitespace, no scheme, and does not turn
 * into any of those once percent-decoded. A still-encoded "%2F..." (an old double-encoded
 * link) is decoded once first, as Auth.tsx always did. /auth (and other login pages) are
 * rejected: sending a signed-in person back to the login page is a loop.
 */
export function sanitizeNext(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  let s = raw.trim();
  if (!s || s.length > 2000) return null;
  if (/^%2F/i.test(s)) {
    try { s = decodeURIComponent(s); } catch { return null; }
  }
  const bad = (v: string) =>
    !v.startsWith("/") ||
    v.startsWith("//") ||
    v.includes("\\") ||
    /[\u0000-\u001f\u007f]/.test(v) ||
    /\s/.test(v.split("?")[0].split("#")[0]) ||
    /^[a-z][a-z0-9+.-]*:/i.test(v);
  if (bad(s)) return null;
  // The same checks on the decoded path: "/%2F%2Fevil.com", "/%5Cevil.com", "/%0d%0a..."
  const pathPart = s.split("?")[0].split("#")[0];
  let decodedPath: string;
  try { decodedPath = decodeURIComponent(pathPart); } catch { return null; }
  if (bad(decodedPath)) return null;
  // A second layer of encoding ("%252F") is refused outright rather than guessed at.
  if (/%25(2f|5c|0[0-9a-f]|1[0-9a-f])/i.test(pathPart)) return null;
  const norm = decodedPath.replace(/\/+$/, "").toLowerCase() || "/";
  if (LOOP_PATHS.has(norm) || norm.startsWith("/auth/")) return null;
  return s;
}

/**
 * T1105 (2026-10-27): what a signup came for, carried on the email link itself. ref, domain and plan were held only in
 * localStorage, so a confirmation link opened in another browser or on a phone lost them (the GEO check's domain, an
 * ad's ref, the plan picked on /pricing). They now ride on the /auth link next to next=, and /auth stores them again
 * when it opens. Only plain values are carried (letters, digits, . _ : - up to 120 characters); anything else is
 * dropped, never passed on. Returns "" or a string starting with "&" for appending to "/auth?verified=true".
 */
export const INTENT_KEYS: ReadonlyArray<readonly [string, string]> = [["ref", "mf_ref"], ["domain", "mf_domain"], ["plan", "mf_plan"]];
export function intentQuery(get: (param: string, storageKey: string) => string | null | undefined): string {
  const out = new URLSearchParams();
  for (const [param, key] of INTENT_KEYS) {
    const v = String(get(param, key) || "").trim();
    if (v && /^[A-Za-z0-9._:-]{1,120}$/.test(v)) out.set(param, v);
  }
  const q = out.toString();
  return q ? "&" + q : "";
}

export type PostAuthDecision =
  | { kind: "next"; path: string; deferOnboarding: boolean; fullLoad: boolean }
  | { kind: "onboarding"; path: "/onboarding" }
  | { kind: "default" };

/**
 * The post-auth order, for every provider (email + password, magic link, LinkedIn, Google):
 *   1. a destination (?next= / ?return_to= on the address, else the stash written before
 *      the provider round trip) -> go there. Onboarding is deferred, never forced first.
 *   2. no destination and onboarding not finished -> /onboarding.
 *   3. otherwise the caller's default landing (Den / dashboard).
 */
export function decidePostAuthRoute(input: {
  next?: string | null;
  storedNext?: string | null;
  onboardingCompleted: boolean;
}): PostAuthDecision {
  const path = sanitizeNext(input.next) || sanitizeNext(input.storedNext);
  if (path) {
    return { kind: "next", path, deferOnboarding: !input.onboardingCompleted, fullLoad: needsFullLoad(path) };
  }
  if (!input.onboardingCompleted) return { kind: "onboarding", path: "/onboarding" };
  return { kind: "default" };
}

/**
 * Whether reaching this path needs a full document load. Most public pages (/pricing, /,
 * /company/..., the landing pages) are static files the React router has never heard of;
 * a client-side navigate() to them renders NotFound. Only the app's own sections stay
 * client-side.
 */
export function needsFullLoad(path: string): boolean {
  return !/^\/(dashboard|onboarding|welcome)(\/|\?|#|$)/.test(path);
}

// ── Browser storage (not pure) ──────────────────────────────────────────────

const NEXT_KEY = "mf_next";
const NEXT_AT_KEY = "mf_next_at";
const NEXT_TTL_MS = 30 * 60 * 1000;
const DEFER_KEY = "mf_onboarding_deferred";
const BANNER_DISMISSED_KEY = "mf_onboarding_banner_dismissed";

/** Keep where the visitor is headed across a provider round trip or an email link (30 minutes). */
export function stashNext(raw: unknown): void {
  const p = sanitizeNext(raw);
  if (!p) return;
  try {
    localStorage.setItem(NEXT_KEY, p);
    localStorage.setItem(NEXT_AT_KEY, String(Date.now()));
  } catch { /* storage blocked: the address still carries ?next= */ }
}

/** The stashed destination if fresh and safe, without consuming it. */
export function peekStoredNext(now: number = Date.now()): string | null {
  try {
    const at = Number(localStorage.getItem(NEXT_AT_KEY) || "0");
    if (at > 0 && now - at < NEXT_TTL_MS) return sanitizeNext(localStorage.getItem(NEXT_KEY));
  } catch { /* ignore */ }
  return null;
}

export function clearStoredNext(): void {
  try { localStorage.removeItem(NEXT_KEY); localStorage.removeItem(NEXT_AT_KEY); } catch { /* ignore */ }
}

/** The router sent an account with unfinished onboarding to its destination: gates stand down this tab session. */
export function markOnboardingDeferred(): void {
  try { sessionStorage.setItem(DEFER_KEY, String(Date.now())); } catch { /* ignore */ }
}

export function onboardingDeferred(): boolean {
  try { return !!sessionStorage.getItem(DEFER_KEY); } catch { return false; }
}

export function clearOnboardingDeferred(): void {
  try { sessionStorage.removeItem(DEFER_KEY); } catch { /* ignore */ }
}

export function onboardingBannerDismissed(): boolean {
  try { return !!sessionStorage.getItem(BANNER_DISMISSED_KEY); } catch { return false; }
}

export function dismissOnboardingBanner(): void {
  try { sessionStorage.setItem(BANNER_DISMISSED_KEY, "1"); } catch { /* ignore */ }
}
